import { createFileRoute, Link } from "@tanstack/react-router";
import { Database, FileSearch, Lock, Mail, Search, ShieldCheck, Upload } from "lucide-react";

import { Helix } from "@/components/helix";
import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Understand Your Genome — your raw DNA data, annotated" },
      {
        name: "description",
        content:
          "Free annotation for your raw DNA data — from 23andMe, AncestryDNA, MyHeritage, FamilyTreeDNA, LivingDNA, or a whole-genome sequencing provider (VCF). We annotate it against public genetic databases for free — frequencies, known associations, database IDs — so you can search and explore your own data.",
      },
      { property: "og:title", content: "Understand Your Genome — your raw DNA data, annotated" },
      {
        property: "og:description",
        content:
          "A free tool by scientists: upload your raw DNA file from any major testing provider (or a whole-genome VCF) and we add technical annotations from public databases so you can search and explore your genetic data without paying for a report.",
      },
    ],
  }),
  component: Landing,
});

function Landing() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 md:grid-cols-[1.15fr_0.85fr] md:py-28">
            <div>
              <h1 className="text-4xl leading-[1.1] sm:text-5xl md:text-6xl">
                You got your DNA results. Now what?
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                Most testing companies hand you a raw data file — or charge extra for a report.
                We're scientists who think the step in between should be freely available: upload
                your file and we'll annotate it against public databases, adding frequencies, known
                associations, and database IDs so you can search and explore your own data without
                looking up every variant by hand.
              </p>
              <p className="mt-6 text-sm text-muted-foreground">
                Works with 23andMe, AncestryDNA, MyHeritage, FamilyTreeDNA, LivingDNA, and
                whole-genome sequencing (VCF) · Free · Educational, never a diagnosis
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button asChild>
                  <Link to="/auth">Upload your file</Link>
                </Button>
                <Button asChild variant="outline">
                  <a href="#how-it-works">How it works</a>
                </Button>
              </div>
            </div>

            <div className="relative hidden justify-center md:flex">
              <div className="absolute inset-0 -z-10 rounded-full bg-primary/8 blur-3xl" />
              <Helix className="h-96 w-auto" />
            </div>
          </div>
        </section>

        {/* What's in your file */}
        <section className="mx-auto max-w-6xl px-5 py-16">
          <div className="grid gap-10 md:grid-cols-2">
            <div>
              <h2 className="text-3xl">What's actually in your file</h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Most providers hand you a <strong className="text-foreground">VCF file</strong> —
                Variant Call Format. A{" "}
                <strong className="text-foreground">variant</strong> is simply a small difference
                between your DNA and the reference human genome, the standard "template" copy
                scientists compare everyone against.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Imagine someone handed you a list of every word where your copy of a book differs
                from the original edition — page numbers, the old word, the new word — and nothing
                else. No note about which changes matter, which are typos, and which everybody
                else's copy has too. That's a raw VCF file.
              </p>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Usually your choice is to pay for a report or wrestle with the raw file yourself.
                We wanted a third option: a free, annotated file you can actually search and
                explore.
              </p>
            </div>

            <div className="rounded-3xl border border-border bg-card p-6 font-mono text-xs leading-relaxed text-muted-foreground shadow-sm">
              <p className="mb-3 font-sans text-sm font-semibold text-foreground">
                A few lines from a typical file
              </p>
              <pre className="overflow-x-auto whitespace-pre">
                {`#CHROM POS       ID          REF ALT
1      11856378  rs1801133   G   A
6      26091179  rs1799945   C   G
10     94781859  rs4244285   G   A
12     21331549  rs4149056   T   C`}
              </pre>
              <p className="mt-4 font-sans text-sm">
                Every row is one difference. Nothing here tells you what any of it means — that
                step is called annotation, and it hasn't happened yet.
              </p>
            </div>
          </div>
        </section>

        {/* What annotation gives you */}
        <section className="bg-secondary/40 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl">We look up the details so you don't have to</h2>
            <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
              <strong className="text-foreground">Annotation</strong> means matching each of your
              differences against what scientists have already published and adding that
              information to your file. You get back an annotated file — not a finished medical
              report — but one where frequencies, database IDs, and known associations are filled
              in. That lets you search for a gene or keyword and see what's known, without
              researching every variant by hand.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                {
                  icon: <Database className="size-5" />,
                  title: "How common it is",
                  body: "Population frequencies — how many other people carry the same difference, added as data for each variant. Many turn out to be shared by millions of people.",
                },
                {
                  icon: <FileSearch className="size-5" />,
                  title: "What it's been linked to",
                  body: "Database identifiers and known associations from the research literature, attached to the matching variants. How strong that evidence is varies a lot from variant to variant.",
                },
                {
                  icon: <ShieldCheck className="size-5" />,
                  title: "Medicine-response hints, where known",
                  body: "Some variants have been linked to faster or slower processing of certain common medications. The evidence varies a lot, and any medication decisions belong with your doctor.",
                },
              ].map((card) => (
                <div key={card.title} className="rounded-3xl border border-border bg-card p-6">
                  <div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {card.icon}
                  </div>
                  <h3 className="mt-4 text-lg">{card.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Every annotation traces back to a named public database — among them{" "}
              <strong className="text-foreground">ClinVar</strong>,{" "}
              <strong className="text-foreground">dbSNP</strong>,{" "}
              <strong className="text-foreground">gnomAD</strong>,{" "}
              <strong className="text-foreground">PharmGKB</strong>, and the{" "}
              <strong className="text-foreground">GWAS Catalog</strong> — 14 in total, the same
              sources researchers and genetic counselors actually cite.
            </p>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Curious about a specific result? Every variant in your report links out to{" "}
              <a
                href="https://www.snpedia.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-dotted underline-offset-2 hover:text-primary"
              >
                SNPedia
              </a>
              , a free, community-run wiki where you can look up almost any SNP by its rsID and see
              what the research literature says — a good next stop once something in your report
              catches your eye.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-16">
          <h2 className="text-3xl">How it works</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {[
              {
                step: "1",
                icon: <Upload className="size-5" />,
                title: "Upload your file",
                body: "Drag in your raw data export — from 23andMe, AncestryDNA, MyHeritage, FamilyTreeDNA, LivingDNA, or a whole-genome VCF. It goes straight into private storage that only your account can open.",
              },
              {
                step: "2",
                icon: <Mail className="size-5" />,
                title: "We annotate it in the background",
                body: "This can take a while — minutes for small files, a few hours for whole genomes. Close the tab; we'll email you the moment it's done.",
              },
              {
                step: "3",
                icon: <FileSearch className="size-5" />,
                title: "Search it, or download it",
                body: "Your file comes back with everything filled in — frequencies, database IDs, known associations. Search it right on the page, or download the whole thing any time. More on that below.",
              },
            ].map((item) => (
              <div key={item.step} className="rounded-3xl border border-border bg-card p-6">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-full bg-accent font-semibold text-accent-foreground">
                    {item.step}
                  </span>
                  <span className="text-primary">{item.icon}</span>
                </div>
                <h3 className="mt-4 text-lg">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* A real, honest preview of what search finds — elaborates on "How it works" step 3 */}
        <section className="mx-auto max-w-5xl px-5 py-16">
          <h2 className="text-3xl">What you get after annotation</h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">
            The moment your file is annotated, a search box lets you type a gene, an rsID, or a
            condition and get an answer in seconds — no spreadsheet, no scrolling through millions
            of rows. Here's exactly the kind of thing it turns up: real public research, quoted
            as-is from <span className="text-foreground">SNPedia</span>. Upload your own file and
            the same search shows your own genotype for questions like these, not just the general
            science.
          </p>
          <div className="mt-6 flex max-w-md items-center gap-2 rounded-2xl border border-border bg-card p-2 shadow-sm">
            <Search className="ml-2 size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-sm text-muted-foreground">lactose</span>
            <span className="rounded-xl bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground">
              Search
            </span>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            That's really all it takes — type a few letters, get an answer.
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {[
              {
                question: "Does cilantro taste like soap to you?",
                gene: "OR10A2",
                snippet:
                  "The A allele of rs72921001 is linked to a lower chance of perceiving cilantro as soapy-tasting — a real, measurable difference in how people experience the same flavor compound.",
              },
              {
                question: "Wet or dry earwax?",
                gene: "ABCC11",
                snippet:
                  "One SNP in the ABCC11 gene decides this — and it's also linked to how much you sweat. It's commonly the (T;T) genotype in East Asian populations, versus (C;C) in European and African populations.",
              },
              {
                question: "What decides blue vs. brown eyes?",
                gene: "HERC2 / OCA2",
                snippet:
                  "A single SNP near the OCA2 gene lowers its activity enough to change eye color outright — the (G;G) genotype at rs12913832 is strongly linked to blue eyes.",
              },
            ].map((card) => (
              <div key={card.question} className="rounded-3xl border border-border bg-card p-6">
                <p className="font-display text-lg">{card.question}</p>
                <p className="mt-1 font-mono text-xs text-muted-foreground">{card.gene}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{card.snippet}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Quoted from{" "}
            <a
              href="https://www.snpedia.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-dotted underline-offset-2 hover:text-primary"
            >
              SNPedia
            </a>
            , a free, community-run wiki — the same source your own search results link out to.
          </p>
        </section>

        {/* Privacy */}
        <section className="bg-secondary/40 py-16">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-2">
            <div>
              <div className="flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Lock className="size-5" />
              </div>
              <h2 className="mt-5 text-3xl">How your data is handled</h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                Genetic data is personal, and we treat it that way. No buried clauses — just what
                actually happens to your file.
              </p>
            </div>
            <ul className="space-y-4">
              {[
                "Your file is encrypted in storage and only reachable through your own signed-in account.",
                "We never sell your data, share it with insurers or employers, or use it to train anything.",
                "You can delete your file and every result generated from it at any time — permanently, not just hidden.",
                "We only email you about your own uploads. No marketing lists.",
              ].map((line) => (
                <li key={line} className="flex gap-3 rounded-2xl border border-border bg-card p-4">
                  <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" />
                  <span className="text-sm leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-5 py-16">
          <h2 className="text-3xl">Questions people ask</h2>
          <Accordion type="single" collapsible className="mt-6">
            <AccordionItem value="formats">
              <AccordionTrigger>Which file formats can I upload?</AccordionTrigger>
              <AccordionContent>
                Raw data exports from 23andMe, AncestryDNA, MyHeritage, FamilyTreeDNA, and LivingDNA
                all work, as-is — no conversion needed on your end, whatever extension your provider
                used (<code className="font-mono text-xs">.txt</code>,{" "}
                <code className="font-mono text-xs">.csv</code>, or otherwise). We also accept
                VCF files (<code className="font-mono text-xs">.vcf</code> /{" "}
                <code className="font-mono text-xs">.vcf.gz</code>) from any source, including
                whole-genome sequencing providers like Dante Labs or Nebula Genomics. Files up to
                1.5 GB — enough for a full whole-genome sequence, not just a consumer DNA-test
                export. If your provider gave you a folder of files, the one you want is usually the
                largest one.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="time">
              <AccordionTrigger>How long does annotation take?</AccordionTrigger>
              <AccordionContent>
                Anywhere from a few minutes to a few hours, depending on how many variants your file
                contains. A consumer DNA export is fast; a whole-genome sequence is a lot more work.
                You don't need to keep the page open — we email you when your annotated file is
                ready.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="diagnosis">
              <AccordionTrigger>Is this a medical diagnosis?</AccordionTrigger>
              <AccordionContent>
                No — and it's not a medical report either. We add publicly available annotations to
                your file so you can explore and research it yourself. That cannot tell you whether
                you have a condition, and it doesn't replace clinical testing. If anything you find
                worries you, bring it to a doctor or a genetic counselor — a specialist trained to
                interpret genetic results in the context of your health and family history.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="data">
              <AccordionTrigger>What happens to my data?</AccordionTrigger>
              <AccordionContent>
                Your file is stored privately under your account and is only readable by you. It is
                never sold, shared, or used to train models. You can delete an individual upload
                from its results page, or wipe everything from your account settings — deletion
                removes the file itself and all results derived from it.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="account">
              <AccordionTrigger>Why do I need to give you an email?</AccordionTrigger>
              <AccordionContent>
                Two reasons: annotation can run for hours, so we need somewhere to send the "it's
                ready" note; and your account lets you return to your uploads from any device —
                with Google, a password, or a one-time email link.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        {/* Sign in */}
        <section className="mx-auto max-w-5xl px-5 pb-8">
          <div className="rounded-4xl border border-border bg-card px-8 py-10 text-center">
            <h2 className="text-2xl">Start with your file</h2>
            <p className="mx-auto mt-3 max-w-lg leading-relaxed text-muted-foreground">
              Uploads are tied to an account so you can come back later. Sign in with your email and
              we'll annotate your VCF for free — built by scientists who want this kind of tool to
              exist for everyone.
            </p>
            <Button asChild className="mt-6">
              <Link to="/auth">Upload your file</Link>
            </Button>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
